import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import "./styles/sistema.css";
import "./styles/print.css";
import "./i18n"; // Initialize i18n before rendering
import { aplicarTamanhoTexto, lerTamanhoTextoSalvo } from "./contexts/TamanhoTextoContext";
import { EVENTO_ATUALIZACAO } from "./lib/atualizacao";

// Tamanho do texto escolhido em Ajustes, aplicado antes do primeiro paint.
aplicarTamanhoTexto(lerTamanhoTextoSalvo());
import { inject } from "@vercel/analytics";

inject();

// ── PWA install prompt: capture as early as possible ──────────────────
// beforeinstallprompt can fire before any component that reads it (Login,
// the Home Android banner) has mounted — bridge it onto window immediately
// so usePWAInstall() never misses it. usePWAInstall reads/writes this same
// global.
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  (window as any).__pwaInstallPrompt = e;
});

const hostname = window.location.hostname;
const isPreview =
  hostname.includes("-preview--") ||
  hostname.startsWith("preview--") ||
  hostname.endsWith(".lovableproject.com") ||
  hostname === "lovableproject.com" ||
  hostname.endsWith(".lovableproject-dev.com") ||
  hostname === "lovableproject-dev.com" ||
  hostname.endsWith(".beta.lovable.dev") ||
  hostname === "beta.lovable.dev" ||
  window.self !== window.top;

// ── Chunk / dynamic-import error auto-recovery ──────────────────────
function isChunkError(msg: string | undefined): boolean {
  if (!msg) return false;
  const lower = msg.toLowerCase();
  return (
    lower.includes("failed to fetch dynamically imported module") ||
    lower.includes("loading chunk") ||
    lower.includes("loading css chunk") ||
    lower.includes("unable to preload") ||
    lower.includes("error loading dynamically imported module")
  );
}

// Evaluated at call-time (not startup) so navigating to /admin after load is protected
function shouldPreserveInProgressState(): boolean {
  return isPreview || window.location.pathname.startsWith("/admin");
}

async function clearSwCachesAndReload() {
  if (shouldPreserveInProgressState()) {
    console.warn("[Recovery] Automatic reload skipped to preserve in-progress edits.");
    return;
  }

  const RECOVERY_KEY = "__glucofy_chunk_recovery";
  const last = sessionStorage.getItem(RECOVERY_KEY);
  // Prevent infinite reload loops: only once every 30s
  if (last && Date.now() - Number(last) < 30_000) return;
  sessionStorage.setItem(RECOVERY_KEY, String(Date.now()));

  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  if ("serviceWorker" in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }
  // Cache-bust para forçar HTML fresh do servidor (não usa cache HTTP do browser)
  const url = new URL(window.location.href);
  url.searchParams.set("_r", Date.now().toString());
  window.location.replace(url.toString());
}

// Global error handlers — catch chunk errors + log everything
window.addEventListener("error", (e) => {
  console.error("[GlobalError]", e.error || e.message);
  if (isChunkError(e.message) || isChunkError(e.error?.message)) {
    clearSwCachesAndReload();
  }
});
window.addEventListener("unhandledrejection", (e) => {
  console.error("[UnhandledRejection]", e.reason);
  const msg =
    typeof e.reason === "string"
      ? e.reason
      : e.reason?.message || String(e.reason);
  if (isChunkError(msg)) {
    clearSwCachesAndReload();
  }
});

async function clearAllSwCaches() {
  if ("caches" in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
}

// ── Versão nova ────────────────────────────────────────────────────
// registerType: "prompt" no vite.config: o service worker novo instala e
// espera (skipWaiting: false) até a gente mandar. Duas situações:
//
//   1. Já havia uma versão esperando quando o app ABRIU: troca na hora e
//      recarrega uma vez, nos primeiros segundos, antes de a pessoa começar
//      qualquer coisa. Pedir "atualizar agora?" para este público era deixar
//      metade da base na versão velha para sempre.
//   2. A versão nova chegou COM o app em uso: não se recarrega uma tela no
//      meio de uma receita. Avisa num cartão grande (AvisoAtualizacao, em
//      React), com botão de 56px, em vez do toast de 13px de antes.
function promptSwUpdate(waitingWorker: ServiceWorker) {
  window.dispatchEvent(new CustomEvent(EVENTO_ATUALIZACAO, { detail: waitingWorker }));
}

function watchForSwUpdates(registration: ServiceWorkerRegistration) {
  // Situação 1: versão esperando desde antes de abrir → troca já.
  if (registration.waiting) registration.waiting.postMessage({ type: "SKIP_WAITING" });

  registration.addEventListener("updatefound", () => {
    const newWorker = registration.installing;
    if (!newWorker) return;
    newWorker.addEventListener("statechange", () => {
      // installed + an already-active worker means this is an update, not the first install
      if (newWorker.state === "installed" && registration.active) {
        promptSwUpdate(newWorker);
      }
    });
  });
}

if ("serviceWorker" in navigator) {
  let reloadedForUpdate = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloadedForUpdate) return;
    reloadedForUpdate = true;
    window.location.reload();
  });
}

// ── Service Worker lifecycle ────────────────────────────────────────
if ("serviceWorker" in navigator) {
  if (isPreview) {
    // In preview: never force reload/replace. Just detach old SW state silently.
    navigator.serviceWorker
      .getRegistrations()
      .then(async (regs) => {
        if (regs.length > 0) {
          await Promise.allSettled(regs.map((reg) => reg.unregister()));
        }
        await clearAllSwCaches();
      })
      .catch(() => {});
  } else {
    // Production only: register the generated Service Worker manually.
    // injectRegister is disabled in vite.config so this is the single
    // registration point, ensuring preview hosts never get an SW.
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => watchForSwUpdates(registration))
        .catch(() => {});
    });
  }
}

// Remove o parâmetro de cache-bust da URL sem recarregar a página
if (window.location.search.includes("_r=")) {
  const url = new URL(window.location.href);
  url.searchParams.delete("_r");
  history.replaceState(null, "", url.toString());
}

createRoot(document.getElementById("root")!).render(<App />);
